import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Lock, Mail, Eye, EyeOff } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { branding, currentTemplate, loadMedia } = useTheme();

  useEffect(() => { loadMedia(); }, [loadMedia]);
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const result = await login(email, password);
      
      if (result.success) {
        toast.success(t('success'), { description: 'Connexion réussie' });
        
        // Redirect based on user role
        const userRole = result.user?.role;
        
        if (userRole === 'super_admin' || userRole === 'admin_ops' || userRole === 'bureau') {
          navigate('/admin', { replace: true });
        } else if (userRole === 'analyst') {
          navigate('/analyst', { replace: true });
        } else if (userRole === 'client') {
          // Check if first login
          if (result.user?.status === 'pending_first_login') {
            navigate('/first-login', { replace: true });
          } else {
            navigate('/client', { replace: true });
          }
        } else {
          navigate('/login', { replace: true });
        }
      } else {
        toast.error(t('error'), { 
          description: result.error || 'Identifiants invalides' 
        });
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(t('error'), { 
        description: error.response?.data?.detail || 'Identifiants invalides' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 lg:px-16 xl:px-24 relative z-10 bg-background">
        <div className="max-w-md mx-auto w-full">
          {/* Logo */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-2">
              {branding.logoUrl ? (
                <img 
                  src={branding.logoUrl} 
                  alt={branding.platformName || 'Logo'} 
                  style={{ height: `${branding.logoSize || 40}px`, width: 'auto' }}
                  className="object-contain"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div 
                  className="rounded flex items-center justify-center"
                  style={{ 
                    backgroundColor: currentTemplate?.colors?.primary || '#0052FF',
                    width: `${branding.logoSize || 40}px`,
                    height: `${branding.logoSize || 40}px`
                  }}
                >
                  <span 
                    className="font-bold"
                    style={{ 
                      color: currentTemplate?.colors?.primaryForeground || '#fff',
                      fontSize: `${Math.max(12, (branding.logoSize || 40) / 3)}px`
                    }}
                  >
                    {branding.platformName?.substring(0, 2).toUpperCase() || 'PB'}
                  </span>
                </div>
              )}
              {branding.showPlatformName !== false && (
                <span className="font-semibold text-xl" style={{ fontFamily: 'var(--font-heading, Playfair Display, serif)' }}>
                  {branding.platformName}
                </span>
              )}
            </div>
            <p className="text-muted-foreground text-sm">
              Espace sécurisé
            </p>
          </div>

          {/* Title */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              {t('login')}
            </h1>
            <p className="text-muted-foreground">
              Accédez à votre espace personnel
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">
                Email ou N° de compte
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="email"
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="votre@email.com"
                  className="pl-10 h-12 bg-card border-border"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">
                {t('password')}
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 pr-10 h-12 bg-card border-border"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 btn-gold text-base"
              data-testid="login-submit-btn"
            >
              {loading ? t('loading') : t('sign_in')}
            </Button>
          </form>

          {/* Footer */}
          <div className="mt-12 pt-8 border-t border-border">
            <p className="text-xs text-muted-foreground text-center">
              © {new Date().getFullYear()} {branding.platformName}. 
              Tous droits réservés.
            </p>
          </div>
        </div>
      </div>

      {/* Right Panel - Image */}
      <div 
        className="hidden lg:block lg:w-1/2 relative"
        style={{
          backgroundImage: `url(${branding.backgroundImage || 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2070'})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/80 to-transparent" />
        <div className="absolute inset-0 bg-black/40" />
      </div>
    </div>
  );
}
